import Keycloak from 'keycloak-js';

interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles: string[];
  department?: string;
  permissions: string[];
}

class KeycloakService {
  private keycloak: Keycloak | null = null;
  private config: KeycloakConfig;
  private tokenRefreshInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.config = {
      url: process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8080',
      realm: process.env.REACT_APP_KEYCLOAK_REALM || 'regal-product-hub',
      clientId: process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'regal-product-hub-frontend',
    };
  }

  /**
   * Initialize Keycloak
   */
  async init(): Promise<boolean> {
    try {
      // Check if we're coming back from a Keycloak redirect (has code or error in URL)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const urlParams = new URLSearchParams(window.location.search);
      const hasCallback = hashParams.has('code') || hashParams.has('error') ||
                         urlParams.has('code') || urlParams.has('error') ||
                         hashParams.has('access_token') || hashParams.has('id_token');

      this.keycloak = new Keycloak(this.config);

      // If we have a callback, use 'login-required' to process it
      // Otherwise use 'check-sso' for silent check
      const onLoadOption = hasCallback ? 'login-required' : 'check-sso';

      // Debug logging (development only)
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Keycloak init - hasCallback:', hasCallback, 'onLoad:', onLoadOption);
        console.log('🔐 URL hash:', window.location.hash);
        console.log('🔐 URL search:', window.location.search);
      }

      const authenticated = await this.keycloak.init({
        onLoad: onLoadOption,
        pkceMethod: 'S256',
        checkLoginIframe: false, // Disable iframe checks to avoid CSP violations
      });

      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Keycloak init result - authenticated:', authenticated);
      }

      // Set up token refresh
      if (authenticated) {
        this.setupTokenRefresh();
        // Clear URL parameters after successful authentication
        if (hasCallback) {
          // Clear hash and query params
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          // Clean up session storage
          sessionStorage.removeItem('keycloak_code_verifier');
          sessionStorage.removeItem('keycloak_state');
        }
      }

      return authenticated;
    } catch (error) {
      // Suppress CSP-related errors as they're expected when checkLoginIframe is false
      if (error instanceof Error && error.message.includes('Content Security Policy')) {
        console.warn('Keycloak CSP warning (expected):', error.message);
        // Continue anyway - authentication will work via redirect
        return false;
      }
      console.error('❌ Keycloak initialization failed:', error);
      return false;
    }
  }

  /**
   * Login - Direct redirect to Keycloak
   */
  async login(): Promise<void> {
    try {
      // Debug logging (development only)
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Login initiated...');
        console.log('🔐 Current URL:', window.location.href);
        console.log('🔐 Keycloak config:', this.config);
      }
      
      // Generate state and code verifier for PKCE
      const state = this.generateRandomString(32);
      const codeVerifier = this.generateRandomString(128);
      const codeChallenge = await this.generateCodeChallenge(codeVerifier);
      
      // Store code verifier in sessionStorage for later use
      sessionStorage.setItem('keycloak_code_verifier', codeVerifier);
      sessionStorage.setItem('keycloak_state', state);
      
      // Build Keycloak authorization URL
      const redirectUri = window.location.origin + window.location.pathname;
      const loginUrl = `${this.config.url}/realms/${this.config.realm}/protocol/openid-connect/auth?` +
        `client_id=${encodeURIComponent(this.config.clientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `state=${encodeURIComponent(state)}&` +
        `response_mode=fragment&` +
        `response_type=code&` +
        `scope=openid&` +
        `code_challenge=${encodeURIComponent(codeChallenge)}&` +
        `code_challenge_method=S256`;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Redirecting to Keycloak:', loginUrl);
      }
      
      // Direct redirect
      window.location.href = loginUrl;
    } catch (error) {
      console.error('❌ Login error:', error);
      // Fallback: simple redirect without PKCE
      const redirectUri = window.location.origin + window.location.pathname;
      const loginUrl = `${this.config.url}/realms/${this.config.realm}/protocol/openid-connect/auth?` +
        `client_id=${encodeURIComponent(this.config.clientId)}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=code&` +
        `scope=openid&` +
        `response_mode=fragment`;
      if (process.env.NODE_ENV === 'development') {
        console.log('🔐 Fallback redirect:', loginUrl);
      }
      window.location.href = loginUrl;
    }
  }

  /**
   * Generate random string for PKCE
   */
  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let result = '';
    const values = new Uint8Array(length);
    crypto.getRandomValues(values);
    for (let i = 0; i < length; i++) {
      result += charset[values[i] % charset.length];
    }
    return result;
  }

  /**
   * Generate code challenge from verifier (SHA256)
   */
  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const bytes = new Uint8Array(digest);
    const base64 = btoa(String.fromCharCode.apply(null, Array.from(bytes)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  /**
   * Logout
   */
  async logout(options?: { redirectUri?: string }): Promise<void> {
    if (this.keycloak) {
      // Clear token refresh interval
      if (this.tokenRefreshInterval) {
        clearInterval(this.tokenRefreshInterval);
        this.tokenRefreshInterval = null;
      }
      // Logout with redirect back to app
      const redirectUri = options?.redirectUri || window.location.origin;
      await this.keycloak.logout({ redirectUri });
    }
  }

  /**
   * Get current token
   */
  getToken(): string | undefined {
    return this.keycloak?.token;
  }

  /**
   * Get user info
   */
  getUserInfo(): AuthUser | null {
    if (!this.keycloak?.authenticated || !this.keycloak.tokenParsed) {
      return null;
    }

    const token = this.keycloak.tokenParsed;
    const roles = this.keycloak.realmAccess?.roles || [];


    return {
      id: token.sub || '',
      email: token.email || '',
      firstName: token.given_name,
      lastName: token.family_name,
      roles: roles,
      department: (token as any).department,
      permissions: (token as any).permissions || [],
    };
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.keycloak?.authenticated || false;
  }

  /**
   * Check if user has role
   */
  hasRole(role: string): boolean {
    if (!this.keycloak) return false;
    return this.keycloak.hasRealmRole(role) || false;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  /**
   * Setup automatic token refresh
   */
  private setupTokenRefresh(): void {
    if (!this.keycloak) return;

    // Clear existing interval if any
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }

    // Check token every minute and refresh if needed (30 seconds before expiry)
    this.tokenRefreshInterval = setInterval(() => {
      if (this.keycloak?.authenticated) {
        this.keycloak.updateToken(30).then(() => {
          // Sync refreshed token to Gobodo API
          const { syncGobodoAuthToken } = require('../gobodo/gobodoClient');
          syncGobodoAuthToken();
        }).catch((error: any) => {
          console.error('Failed to refresh token:', error);
          // In development, don't logout on refresh failure (Keycloak may not be reachable)
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ Token refresh failed in dev mode - continuing without logout');
            return;
          }
          // If refresh fails in production, logout user
          this.logout();
        });
      }
    }, 60000); // Check every minute
  }

  /**
   * Get Keycloak instance (for advanced usage)
   */
  getKeycloak(): Keycloak | null {
    return this.keycloak;
  }

  /**
   * Register callback for token updates
   */
  onTokenExpired(callback: () => void): void {
    if (this.keycloak) {
      this.keycloak.onTokenExpired = callback;
    }
  }

  /**
   * Register callback for auth success
   */
  onAuthSuccess(callback: () => void): void {
    if (this.keycloak) {
      this.keycloak.onAuthSuccess = callback;
    }
  }

  /**
   * Register callback for auth error
   */
  onAuthError(callback: () => void): void {
    if (this.keycloak) {
      this.keycloak.onAuthError = callback;
    }
  }
}

export const keycloakService = new KeycloakService();
export type { AuthUser };

